/**
 * intake-store.ts — localStorage persistence for the Rapid Intake screen.
 *
 * Storage key: aip:rapid-intake:v1
 *
 * Shape is intentionally designed to round-trip with Supabase later:
 *   - quotas    → quotas table
 *   - deals     → closed_deals table
 *   - pipeline  → pipeline_opps table
 */

// ─── Types ─────────────────────────────────────────────────────────────────────

export type AeName = string;

export type QuotaInputMode = "annual" | "quarterly" | "monthly";

export interface QuotaEntry {
  aeId: string;                         // slug, e.g. "reed"
  aeName: AeName;
  effectiveYear: number;                // e.g. 2026

  quotaInputMode: QuotaInputMode;       // which mode the AE used

  // Raw input — whichever mode was used; others may be null
  quotaAnnual: number | null;           // entered directly in annual mode
  quotaQuarterly: [number, number, number, number] | null; // Q1–Q4

  // Always populated (derived from whichever mode was used)
  // Even-distribution rationale: floating to last month of quarter creates
  // artificial pipeline-coverage spikes mid-quarter; even distribution gives
  // honest week-by-week tracking.
  quotaMonthly: [
    number, number, number,   // Jan Feb Mar
    number, number, number,   // Apr May Jun
    number, number, number,   // Jul Aug Sep
    number, number, number,   // Oct Nov Dec
  ];

  savedAt: number;
}

// ─── Quota derivation helpers ─────────────────────────────────────────────────

/** Derive a 12-month array from annual (÷ 12, evenly) */
export function deriveMonthlyFromAnnual(annual: number): QuotaEntry["quotaMonthly"] {
  const mo = Math.round(annual / 12);
  // distribute remainder to Jan to make sum exact
  const base = Array(12).fill(mo) as number[];
  const diff = annual - mo * 12;
  base[0] += diff;
  return base as QuotaEntry["quotaMonthly"];
}

/** Derive a 12-month array from 4 quarterly numbers (even distribution per quarter) */
export function deriveMonthlyFromQuarterly(
  q: [number, number, number, number],
): QuotaEntry["quotaMonthly"] {
  const months: number[] = [];
  q.forEach((qv) => {
    const mo = Math.round(qv / 3);
    const rem = qv - mo * 3;
    months.push(mo + rem, mo, mo); // push remainder into first month of quarter
  });
  return months as QuotaEntry["quotaMonthly"];
}

/** Sum a monthly array to get annual */
export function annualFromMonthly(monthly: QuotaEntry["quotaMonthly"]): number {
  return monthly.reduce((s, m) => s + m, 0);
}

/** Sum monthly array by quarter */
export function quarterlyFromMonthly(
  monthly: QuotaEntry["quotaMonthly"],
): [number, number, number, number] {
  return [
    monthly[0] + monthly[1] + monthly[2],
    monthly[3] + monthly[4] + monthly[5],
    monthly[6] + monthly[7] + monthly[8],
    monthly[9] + monthly[10] + monthly[11],
  ];
}

// ─── Deal / pipeline types ────────────────────────────────────────────────────

export type ProductOption =
  | "Attilius — Need"
  | "Attilius — Budget"
  | "Attilius — Authority"
  | "Attilius — Timeline"
  | "Attilius — Solution"
  | "Attilius — Decision"
  | "Multi-module Bundle"
  | "Other";

export const PRODUCT_OPTIONS: ProductOption[] = [
  "Attilius — Need",
  "Attilius — Budget",
  "Attilius — Authority",
  "Attilius — Timeline",
  "Attilius — Solution",
  "Attilius — Decision",
  "Multi-module Bundle",
  "Other",
];

export type DealType = "New" | "Expansion" | "Renewal";

export interface ClosedDeal {
  id: string;
  dateClosed: string;               // ISO date string YYYY-MM-DD
  accountName: string;
  product: ProductOption;
  dealType: DealType;
  oneTimeAmount: number;            // $
  recurringAmount: number;          // $ per period
  recurringPeriod: "month" | "year";
  contractLengthMonths: number;
  savedAt: number;
}

export type StageKey =
  | "CP1" | "CP2" | "CP3" | "CP4" | "CP5"
  | "CP6" | "CP7" | "CP8" | "CP9" | "CP10";

export const STAGE_LABELS: Record<StageKey, string> = {
  CP1:  "CP1 — Project",
  CP2:  "CP2 — Need / Pain / Demonstration",
  CP3:  "CP3 — Budget",
  CP4:  "CP4 — Timeline",
  CP5:  "CP5 — Authority — Project / Initiative",
  CP6:  "CP6 — Technical and Financial Sign-off",
  CP7:  "CP7 — Org Structure and Influence Map",
  CP8:  "CP8 — Competitive Position",
  CP9:  "CP9 — Compliance",
  CP10: "CP10 — Legal",
};

export const STAGE_MULTIPLIERS: Record<StageKey, number> = {
  CP1: 0.05, CP2: 0.10, CP3: 0.15, CP4: 0.25, CP5: 0.35,
  CP6: 0.45, CP7: 0.60, CP8: 0.75, CP9: 0.85, CP10: 0.92,
};

export const STAGE_KEYS = Object.keys(STAGE_LABELS) as StageKey[];

export interface PipelineOpp {
  id: string;
  accountName: string;
  product: ProductOption;
  stage: StageKey;
  expectedCloseDate: string;        // ISO date string YYYY-MM-DD
  dealSize: number;                 // $
  recurringAmount: number;          // $/mo
  scorePct: number;                 // 0–100; default = stage multiplier × 100
  savedAt: number;
}

// ─── Top-level store shape ────────────────────────────────────────────────────

export interface IntakeStore {
  quotas: QuotaEntry[];
  closedDeals: ClosedDeal[];
  pipeline: PipelineOpp[];
}

// ─── Storage key ───────────────────────────────────────────────────────────────

const STORAGE_KEY = "aip:rapid-intake:v1";

// ─── Load / save ───────────────────────────────────────────────────────────────

export function loadStore(): IntakeStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { quotas: [], closedDeals: [], pipeline: [] };
    const parsed = JSON.parse(raw) as IntakeStore;
    return {
      quotas:      parsed.quotas      ?? [],
      closedDeals: parsed.closedDeals ?? [],
      pipeline:    parsed.pipeline    ?? [],
    };
  } catch {
    return { quotas: [], closedDeals: [], pipeline: [] };
  }
}

export function saveStore(store: IntakeStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    console.warn("intake-store: failed to persist to localStorage", e);
  }
}

// ─── Quota CRUD ───────────────────────────────────────────────────────────────

export function getQuota(store: IntakeStore, aeId: string): QuotaEntry | null {
  return store.quotas.find((q) => q.aeId === aeId) ?? null;
}

export function upsertQuota(store: IntakeStore, entry: QuotaEntry): IntakeStore {
  const idx = store.quotas.findIndex((q) => q.aeId === entry.aeId);
  const updated = [...store.quotas];
  if (idx >= 0) updated[idx] = entry;
  else updated.push(entry);
  return { ...store, quotas: updated };
}

// ─── Closed deal CRUD ─────────────────────────────────────────────────────────

export function addClosedDeal(store: IntakeStore, deal: ClosedDeal): IntakeStore {
  return { ...store, closedDeals: [...store.closedDeals, deal] };
}

export function updateClosedDeal(store: IntakeStore, deal: ClosedDeal): IntakeStore {
  return { ...store, closedDeals: store.closedDeals.map((d) => d.id === deal.id ? deal : d) };
}

export function deleteClosedDeal(store: IntakeStore, id: string): IntakeStore {
  return { ...store, closedDeals: store.closedDeals.filter((d) => d.id !== id) };
}

export function totalContractValue(deal: ClosedDeal): number {
  const recurringMonthly = deal.recurringPeriod === "year"
    ? deal.recurringAmount / 12
    : deal.recurringAmount;
  return deal.oneTimeAmount + recurringMonthly * deal.contractLengthMonths;
}

// ─── Pipeline CRUD ────────────────────────────────────────────────────────────

export function addPipelineOpp(store: IntakeStore, opp: PipelineOpp): IntakeStore {
  return { ...store, pipeline: [...store.pipeline, opp] };
}

export function updatePipelineOpp(store: IntakeStore, opp: PipelineOpp): IntakeStore {
  return { ...store, pipeline: store.pipeline.map((p) => p.id === opp.id ? opp : p) };
}

export function deletePipelineOpp(store: IntakeStore, id: string): IntakeStore {
  return { ...store, pipeline: store.pipeline.filter((p) => p.id !== id) };
}

export function weightedForecast(opp: PipelineOpp): number {
  const mult = STAGE_MULTIPLIERS[opp.stage] ?? 0;
  return opp.dealSize * (opp.scorePct / 100) * mult;
}

export function totalWeightedForecast(pipeline: PipelineOpp[]): number {
  return pipeline.reduce((sum, opp) => sum + weightedForecast(opp), 0);
}

// ─── Misc helpers ─────────────────────────────────────────────────────────────

export function totalClosedArr(deals: ClosedDeal[]): number {
  return deals.reduce((s, d) => {
    const monthly = d.recurringPeriod === "year" ? d.recurringAmount / 12 : d.recurringAmount;
    return s + monthly * 12 + d.oneTimeAmount;
  }, 0);
}

export function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function fmt$(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

export const MONTH_LABELS = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec",
];

export const AE_OPTIONS: AeName[] = ["Your Name"];
